import { i18n } from 'shared/i18n/i18n';
import { KitElement } from 'shared/kit/kit-element';
import { DEFAULT_COLOR_2, KitOutfit, makeDefaultElementState } from 'shared/kit/kit-outfit';
import { KitPattern, KIT_PATTERN_COLOR_COUNT, KIT_PATTERN_LABELS } from 'shared/kit/kit-pattern';
import { ALL_KIT_TYPES, KitType } from 'shared/kit/kit-type';
import { TeamKit } from 'shared/kit/team-kit';
import { SelectOption } from 'ud-form/components/select/component';

export type AllKitState = Record<KitType, KitOutfit>;

export function buildInitialState(kits: TeamKit[], allowedElements: KitElement[]): AllKitState {
  const initial = {} as AllKitState;
  for (const kitType of ALL_KIT_TYPES) {
    const kitState = {} as KitOutfit;
    for (const el of allowedElements) {
      kitState[el] = makeDefaultElementState();
    }
    const existingKit = kits.find((k) => k.type === kitType);
    if (existingKit) {
      for (const kitEl of existingKit.elements) {
        if (allowedElements.includes(kitEl.element)) {
          kitState[kitEl.element] = {
            pattern: kitEl.attributes.pattern,
            colors: [...kitEl.attributes.colors],
          };
        }
      }
    }
    initial[kitType] = kitState;
  }
  return initial;
}

// ── Значения формы ─────────────────────────────────────────────────────────
//
// `UDFormSelect` держит в Formik объект опции, а не строку, поэтому значения
// формы отличаются от `KitOutfit`: `pattern` здесь — `SelectOption<KitPattern>`.
// Преобразование в обе стороны живёт тут же, рядом с `buildInitialState`.

export interface KitFormElementValues {
  pattern: SelectOption<KitPattern>;
  colors: string[];
}

export type KitFormKitValues = Record<KitElement, KitFormElementValues>;
export type KitFormValues = Record<KitType, KitFormKitValues>;

/**
 * Подпись переводится в момент вызова. Опция узора лежит в значениях Formik строкой, поэтому
 * смена языка поменяет подпись уже выбранного узора только после переинициализации формы.
 */
export function patternOption(pattern: KitPattern): SelectOption<KitPattern> {
  return { value: pattern, label: i18n._(KIT_PATTERN_LABELS[pattern]) };
}

/** Опции селекта узора — в порядке объявления enum-а, на текущем языке. */
export const getPatternOptions = (): SelectOption<KitPattern>[] => Object.values(KitPattern).map(patternOption);

/** Дополняет или обрезает набор цветов под выбранный узор. */
export function fitColorsToPattern(colors: string[], pattern: KitPattern): string[] {
  const required = KIT_PATTERN_COLOR_COUNT[pattern];
  const next = [...colors];
  while (next.length < required) next.push(DEFAULT_COLOR_2);
  next.splice(required);
  return next;
}

export function buildInitialValues(kits: TeamKit[], allowedElements: KitElement[]): KitFormValues {
  const state = buildInitialState(kits, allowedElements);
  const values = {} as KitFormValues;

  for (const kitType of ALL_KIT_TYPES) {
    const kitValues = {} as KitFormKitValues;
    for (const element of allowedElements) {
      const elementState = state[kitType][element];
      kitValues[element] = { pattern: patternOption(elementState.pattern), colors: [...elementState.colors] };
    }
    values[kitType] = kitValues;
  }

  return values;
}

/** Значения комплекта → состояние для превью и свотча. */
export function toElementStates(kitValues: Partial<KitFormKitValues> = {}): Partial<KitOutfit> {
  const states: Partial<KitOutfit> = {};

  for (const [element, value] of Object.entries(kitValues) as [KitElement, KitFormElementValues][]) {
    states[element] = { pattern: value.pattern.value, colors: value.colors };
  }

  return states;
}
